/**
 * User Verification – Level display (dashboard-wt theme)
 * Shows current level, progress, and level cards (Unverified → Phone → KYC).
 */
import React, { useEffect, useState } from 'react';
import { useUser } from '../../hooks/useUser';
import { useI18n } from '../../providers';
import Spinner from '../common/Spinner';

const UserVerification = ({ onVerify }) => {
  const { loadVerificationStatus, verificationStatus, loading } = useUser();
  const { language } = useI18n();

  useEffect(() => {
    loadVerificationStatus();
  }, [loadVerificationStatus]);

  if (loading && !verificationStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const currentLevel = verificationStatus?.level ?? 0;
  const progressPct = (currentLevel / 2) * 100;

  const levels = [
    {
      level: 0,
      name: language === 'fr' ? 'Non vérifié' : 'Unverified',
      description: language === 'fr' ? 'Niveau de base - Transactions limitées' : 'Basic level - Limited transactions',
      icon: '🔓',
      completed: currentLevel >= 0,
      isCurrent: currentLevel === 0,
    },
    {
      level: 1,
      name: language === 'fr' ? 'Vérifié par téléphone' : 'Phone Verified',
      description: language === 'fr' ? 'Vérification par SMS - Transactions moyennes' : 'SMS verification - Medium transactions',
      icon: '📱',
      completed: currentLevel >= 1,
      isCurrent: currentLevel === 1,
    },
    {
      level: 2,
      name: language === 'fr' ? 'KYC Vérifié' : 'KYC Verified',
      description: language === 'fr' ? 'Vérification d\'identité complète - Toutes transactions' : 'Full identity verification - All transactions',
      icon: '🆔',
      completed: currentLevel >= 2,
      isCurrent: currentLevel === 2,
    },
  ];

  const currentLevelData = levels[currentLevel] ?? levels[0];
  const nextLevel = levels.find((l) => l.level > currentLevel);

  return (
    <div className="space-y-6">
      {/* Current status */}
      <div className="text-center">
        <div
          className={`inline-flex h-16 w-16 items-center justify-center rounded-2xl text-3xl ${
            currentLevelData.completed && currentLevel > 0
              ? 'border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.12)]'
              : 'border border-[rgba(255,255,255,0.12)] bg-[#1a1a1a]'
          }`}
        >
          {currentLevelData.icon}
        </div>
        <h3 className="mt-4 text-xl font-bold text-[#fafafa]">
          {currentLevelData.name}
        </h3>
        <p className="mt-1 text-sm text-[#a3a3a3]">
          {currentLevelData.description}
        </p>
      </div>

      {/* Progress */}
      <div>
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium text-[#e5e5e5]">
            {language === 'fr' ? 'Niveau' : 'Level'} {currentLevel}/2
          </span>
          <span className="text-[#a3a3a3]">{Math.round(progressPct)}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-[#262626]">
          <div
            className="h-full rounded-full bg-[#22c55e] transition-all duration-500 ease-out"
            style={{ width: `${progressPct}%` }}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      {/* Level cards */}
      <div className="space-y-3">
        {levels.map((level) => (
          <div
            key={level.level}
            className={`flex items-center gap-4 rounded-xl border p-4 transition-colors ${
              level.completed
                ? 'border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.06)]'
                : level.isCurrent
                  ? 'border-[rgba(255,255,255,0.15)] bg-[#1a1a1a]'
                  : 'border-[rgba(255,255,255,0.08)] bg-[#111]'
            }`}
          >
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl ${
                level.completed ? 'bg-[rgba(34,197,94,0.2)]' : 'bg-[#262626]'
              }`}
            >
              {level.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-[#fafafa]">{level.name}</h4>
                {level.completed && (
                  <span className="text-[#22c55e]" aria-hidden>✓</span>
                )}
              </div>
              <p className="mt-0.5 text-sm text-[#a3a3a3]">{level.description}</p>
            </div>
            {level.completed && (
              <span className="shrink-0 rounded-full border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.12)] px-2.5 py-1 text-xs font-medium text-[#22c55e]">
                {language === 'fr' ? 'Complété' : 'Completed'}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* CTA */}
      {nextLevel && onVerify && (
        <button
          type="button"
          onClick={onVerify}
          className="dashboard-wt-btn-primary w-full py-3"
        >
          {language === 'fr' ? 'Vérifier maintenant' : 'Verify Now'} →
        </button>
      )}
    </div>
  );
};

export default UserVerification;
